import { Link } from "@/components/link";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { useAuthActions } from "@convex-dev/auth/react";
import { api } from "../../convex/_generated/api";

import * as React from "react";
import { ChevronsUpDown, Moon, Search, Sun } from "lucide-react";
import { useTheme } from "next-themes";

import { navItems, workspace } from "@/lib/dashboard-data";
import { navIconMap } from "@/components/nav-icons";
import { useIsMobile } from "@/hooks/use-mobile";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";

function initials(name: string) {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const pathname = useLocation({ select: (s) => s.pathname });
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const me = useQuery(api.users.currentUser);
  const displayName = me?.name ?? me?.email?.split("@")[0] ?? "Loading…";
  const displayEmail = me?.email ?? "";
  // The header's theme toggle is hidden on phones, so it lives in this menu there.
  const isMobile = useIsMobile();
  const { resolvedTheme, setTheme } = useTheme();

  const handleSignOut = async () => {
    await signOut();
    navigate({ to: "/login", replace: true });
  };
  return (
    <Sidebar variant="inset" collapsible="offcanvas" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<Link href="/" />}>
              <img src="/logo.png" alt="" className="size-8 shrink-0" />
              <div className="grid min-w-0 flex-1 text-left leading-tight">
                <span className="font-medium wrap-break-word">{workspace.name}</span>
                <span className="text-xs text-muted-foreground wrap-break-word">
                  {workspace.plan}
                </span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navItems.map((item) => {
              const Icon = navIconMap[item.icon];
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    isActive={
                      item.href === "/"
                        ? pathname === "/"
                        : pathname === item.href || pathname.startsWith(`${item.href}/`)
                    }
                    render={<Link href={item.href} />}
                  >
                    <Icon />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() =>
                window.dispatchEvent(new CustomEvent("dashboard:open-search"))
              }
            >
              <Search />
              <span>Search</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger render={<SidebarMenuButton size="lg" />}>
                <Avatar className="shrink-0">
                  <AvatarImage src={me?.image} alt={displayName} />
                  <AvatarFallback>{initials(displayName)}</AvatarFallback>
                </Avatar>
                <div className="grid min-w-0 flex-1 text-left leading-tight">
                  <span className="font-medium wrap-break-word">
                    {displayName}
                    {me?.isAdmin === true && (
                      <span className="text-muted-foreground"> · Admin</span>
                    )}
                  </span>
                  {displayEmail && (
                    <span className="text-xs text-muted-foreground wrap-anywhere">
                      {displayEmail}
                    </span>
                  )}
                </div>
                <ChevronsUpDown className="ml-auto shrink-0" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                side="top"
                sideOffset={4}
                className="w-(--anchor-width) min-w-56"
              >
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex items-center gap-2 py-1 text-left text-sm">
                      <Avatar className="shrink-0">
                        <AvatarImage src={me?.image} alt={displayName} />
                        <AvatarFallback>{initials(displayName)}</AvatarFallback>
                      </Avatar>
                      <div className="grid min-w-0 flex-1 leading-tight">
                        <span className="font-medium text-foreground wrap-break-word">
                          {displayName}
                        </span>
                        {displayEmail && (
                          <span className="text-xs text-muted-foreground wrap-anywhere">
                            {displayEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </DropdownMenuLabel>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem render={<Link href="/settings" />}>
                  Account settings
                </DropdownMenuItem>
                {isMobile ? (
                  <DropdownMenuItem
                    closeOnClick={false}
                    onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
                  >
                    {resolvedTheme === "dark" ? <Sun /> : <Moon />}
                    {resolvedTheme === "dark" ? "Light mode" : "Dark mode"}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSignOut}>
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
